import { getTokenSync } from "@common/auth/bearer-token";
import { nativePlaybackBridge } from "@common/core/capacitor/native-playback-bridge";
import {
  createEmptyPlaybackState,
} from "@common/core/capacitor/native-playback-contract";
import type {
  NativePlaybackConfig,
  NativePlaybackLanguageOptions,
  NativePlaybackStateResponse,
  PlaybackItem,
  PlaybackState,
  QueueSnapshot,
  RemoteCommandReceivedEvent,
  RepeatMode as NativeRepeatMode,
  StreamProtocol,
} from "@common/core/capacitor/native-playback-contract";
import { getTrackImageSrc } from "@app/web-player/media-image/media-image";
import { apiClient } from "@common/http/query-client";
import { usePlayerStore } from "@common/player/hooks/use-player-store";
import type { MediaItem } from "@common/player/media-item";
import { PlayerStoreContext } from "@common/player/player-context";
import { getUserRolesAccess } from "@common/auth/user-roles-access";
import { isSameMedia } from "@common/player/utils/is-same-media";
import {
  canUseNativeAudioPlaybackForMedia,
  canUseNativeAudioPlaybackForQueue,
} from "@common/player/utils/can-use-native-audio-provider";
import { withAssetUrl } from "@common/player/utils/with-asset-url";
import type { PlayerProviderApi } from "@common/player/state/player-provider-api";
import type {
  MediaStreamType,
  PlayerState as PlayerStoreState,
  RepeatMode as StoreRepeatMode,
} from "@common/player/state/player-state";
import { mapNativeLimitReachedEvent } from "@common/player/limits/native-limit-reached";
import { getBootstrapData } from "@ui/bootstrap-data/bootstrap-data-store";
import { useSelectedLocale } from "@ui/i18n/selected-locale";
import { useSettings } from "@ui/settings/use-settings";
import { useAppEventManager } from "@common/core/capacitor/app-event-manager";
import { useCallback, useContext, useEffect, useMemo, useRef } from "react";

const defaultPlaybackRates = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];
const NATIVE_PLAYBACK_END_SETTLE_DELAY_MS = 150;
const NATIVE_REMOTE_COMMAND_SYNC_DELAY_MS = 200;
const DEBUG_NATIVE_PLAYBACK = false;

type SuppressedNativeBridgeCommand =
  | { command: "play"; consumed: boolean }
  | { command: "pause"; consumed: boolean }
  | { command: "stop"; consumed: boolean }
  | { command: "next"; consumed: boolean }
  | { command: "previous"; consumed: boolean }
  | { command: "seekTo"; positionMs: number; consumed: boolean }
  | { command: "skipToIndex"; index: number; consumed: boolean };

function logNativePlayback(message: string, payload?: unknown) {
  if (!DEBUG_NATIVE_PLAYBACK || typeof console === "undefined") {
    return;
  }

  if (typeof payload === "undefined") {
    console.info(message);
  } else {
    console.info(message, payload);
  }
}

export function NativeAudioProvider() {
  const store = useContext(PlayerStoreContext);
  const cuedMedia = usePlayerStore((s) => s.cuedMedia);
  const providerName = usePlayerStore((s) => s.providerName);
  const shuffledQueue = usePlayerStore((s) => s.shuffledQueue);
  const shuffling = usePlayerStore((s) => s.shuffling);
  const repeat = usePlayerStore((s) => s.repeat);
  const controlsLocked = usePlayerStore((s) => s.controlsLocked);
  const { localeCode, lines } = useSelectedLocale();
  const {
    branding: { site_name },
  } = useSettings();

  const playbackStateRef = useRef<PlaybackState | null>(null);
  const queueSnapshotRef = useRef<QueueSnapshot | null>(null);
  const nativeQueueRef = useRef<MediaItem[]>([]);
  const queueSyncPromiseRef = useRef<Promise<void> | null>(null);
  const pendingFrontendQueueSignatureRef = useRef<string | null>(null);
  const lastCueSignatureRef = useRef<string | null>(null);
  const lastNativeQueueSignatureRef = useRef<string | null>(null);
  const activeQueueSyncRunIdRef = useRef<symbol | null>(null);
  const lastLanguageSignatureRef = useRef<string | null>(null);
  const languageOptionsRef = useRef<NativePlaybackLanguageOptions | null>(null);
  const hasConfiguredSessionRef = useRef(false);
  const hasBootstrappedNativeStateRef = useRef(false);
  const hasProcessedInitialNativeQueueRef = useRef(false);
  const isHydratingFromNativeRef = useRef(false);
  const nativeEventListenersBoundRef = useRef(false);
  const scheduledPostSyncRefreshRef = useRef<symbol | null>(null);
  const scheduledNativeSubscriptionRetryRef = useRef<symbol | null>(null);
  const pendingPlaybackEndCheckRef = useRef<symbol | null>(null);
  const pendingNativeQueueReconcileRef = useRef<symbol | null>(null);
  const providerElementRef = useRef<HTMLElement | null>(null);
  const lastFrontendMirroredQueueStructureRef = useRef<string | null>(null);
  const suppressedNativeBridgeCommandRef =
    useRef<SuppressedNativeBridgeCommand | null>(null);
  const nativeQueueAuthorityRef = useRef<"unknown" | "frontend" | "native">(
    "unknown",
  );

  if (!providerElementRef.current) {
    providerElementRef.current =
      typeof document === "undefined"
        ? ({} as HTMLElement)
        : document.createElement("div");
  }

  const runNativeQueueCommand = useCallback(
    async (
      action: () => Promise<void>,
      options?: { authority?: "frontend" | "native" },
    ) => {
      const syncRunId = Symbol("nativeQueueCommand");
      activeQueueSyncRunIdRef.current = syncRunId;
      pendingFrontendQueueSignatureRef.current = null;
      if (options?.authority) {
        nativeQueueAuthorityRef.current = options.authority;
      }

      const commandPromise = action();
      queueSyncPromiseRef.current = commandPromise.then(
        () => undefined,
        () => undefined,
      );

      try {
        await commandPromise;
      } catch (error) {
        store.getState().emit("error", { sourceEvent: error });
        throw error;
      } finally {
        if (activeQueueSyncRunIdRef.current === syncRunId) {
          activeQueueSyncRunIdRef.current = null;
          queueSyncPromiseRef.current = null;
        }
      }
    },
    [store],
  );

  const suppressNativeBridgeCommand = useCallback(
    (command: SuppressedNativeBridgeCommand) => {
      suppressedNativeBridgeCommandRef.current = command;
    },
    [],
  );

  const consumeSuppressedNativeBridgeCommand = useCallback(
    (
      command: SuppressedNativeBridgeCommand["command"],
      options?: { positionMs?: number; index?: number },
    ) => {
      const pending = suppressedNativeBridgeCommandRef.current;
      if (!pending || pending.command !== command) {
        return false;
      }

      if (pending.command === "seekTo") {
        const actualPositionMs = options?.positionMs;
        if (
          typeof actualPositionMs !== "number" ||
          Math.abs(pending.positionMs - actualPositionMs) > 750
        ) {
          return false;
        }
      }

      if (pending.command === "skipToIndex") {
        if (pending.index !== options?.index) {
          return false;
        }
      }

      pending.consumed = true;
      suppressedNativeBridgeCommandRef.current = null;
      return true;
    },
    [],
  );

  const providerApi = useMemo<PlayerProviderApi>(
    () => ({
      play: () => {
        if (consumeSuppressedNativeBridgeCommand("play")) {
          return;
        }
        void (async () => {
          await queueSyncPromiseRef.current;
          await nativePlaybackBridge.play();
        })().catch((error) => {
          store.getState().emit("error", { sourceEvent: error });
        });
      },
      pause: () => {
        if (consumeSuppressedNativeBridgeCommand("pause")) {
          return;
        }
        void nativePlaybackBridge.pause().catch((error) => {
          store.getState().emit("error", { sourceEvent: error });
        });
      },
      stop: () => {
        if (consumeSuppressedNativeBridgeCommand("stop")) {
          return;
        }
        void nativePlaybackBridge.stop().catch((error) => {
          store.getState().emit("error", { sourceEvent: error });
        });
      },
      seek: (time: number) => {
        const positionMs = Math.max(0, Math.round(time * 1000));
        if (
          consumeSuppressedNativeBridgeCommand("seekTo", { positionMs })
        ) {
          return;
        }

        void nativePlaybackBridge
          .seekTo({ positionMs })
          .catch((error) => {
            store.getState().emit("error", { sourceEvent: error });
          });
      },
      skipToNext: () => {
        if (consumeSuppressedNativeBridgeCommand("next")) {
          return;
        }
        void nativePlaybackBridge.skipToNext().catch((error) => {
          store.getState().emit("error", { sourceEvent: error });
        });
      },
      skipToPrevious: () => {
        if (consumeSuppressedNativeBridgeCommand("previous")) {
          return;
        }
        void nativePlaybackBridge.skipToPrevious().catch((error) => {
          store.getState().emit("error", { sourceEvent: error });
        });
      },
      skipToIndex: (index: number) => {
        if (consumeSuppressedNativeBridgeCommand("skipToIndex", { index })) {
          return;
        }
        void nativePlaybackBridge.skipToIndex({ index }).catch((error) => {
          store.getState().emit("error", { sourceEvent: error });
        });
      },
      cueMedia: (media: MediaItem) => {
        const item = toPlaybackItem(media);
        if (!item) {
          return;
        }

        return runNativeQueueCommand(
          () => nativePlaybackBridge.cue(item),
          { authority: "frontend" },
        );
      },
      replaceQueue: (
        queue: MediaItem[],
        startIndex = 0,
        autoPlay = false,
      ) => {
        const entries = getNativeQueueEntries(queue);
        if (!entries.length) {
          return;
        }

        const targetMedia = queue[startIndex] ?? entries[0]?.media;
        const nativeStartIndex = Math.max(
          0,
          entries.findIndex((entry) =>
            targetMedia ? isSameMedia(entry.media, targetMedia) : false,
          ),
        );

        return runNativeQueueCommand(
          () =>
            nativePlaybackBridge.replaceQueue({
              items: entries.map((entry) => entry.item),
              startIndex: nativeStartIndex,
              autoPlay,
            }),
          { authority: "frontend" },
        );
      },
      appendToQueue: (items: MediaItem[], afterCurrent = true) => {
        const entries = getNativeQueueEntries(items);
        if (!entries.length) {
          return;
        }

        return runNativeQueueCommand(
          () =>
            nativePlaybackBridge.appendToQueue({
              items: entries.map((entry) => entry.item),
              afterCurrent,
            }),
          { authority: "frontend" },
        );
      },
      addToQueue: (items: MediaItem[], index: number) => {
        const entries = getNativeQueueEntries(items);
        if (!entries.length) {
          return;
        }

        return runNativeQueueCommand(
          () =>
            nativePlaybackBridge.addToQueue({
              items: entries.map((entry) => entry.item),
              index,
            }),
          { authority: "frontend" },
        );
      },
      removeFromQueue: (items: MediaItem[]) => {
        const removableItems = items.map((item) => ({
          id: item.id,
          ...(item.groupId != null ? { groupId: item.groupId } : {}),
        }));
        if (!removableItems.length) {
          return;
        }

        return runNativeQueueCommand(
          () => nativePlaybackBridge.removeFromQueue({ items: removableItems }),
          { authority: "frontend" },
        );
      },
      setRepeatMode: (mode) => {
        void nativePlaybackBridge
          .setRepeatMode({ mode: toNativeRepeatMode(mode) })
          .catch((error) => {
            store.getState().emit("error", { sourceEvent: error });
          });
      },
      setShuffle: (enabled) => {
        void nativePlaybackBridge.setShuffle({ enabled }).catch((error) => {
          store.getState().emit("error", { sourceEvent: error });
        });
      },
      setVolume: (value: number) => {
        void nativePlaybackBridge
          .setVolume({ volume: value })
          .catch((error) => {
            store.getState().emit("error", { sourceEvent: error });
          });
      },
      setMuted: (isMuted: boolean) => {
        void nativePlaybackBridge
          .setMuted({ muted: isMuted })
          .catch((error) => {
            store.getState().emit("error", { sourceEvent: error });
          });
      },
      setPlaybackRate: (value: number) => {
        void nativePlaybackBridge
          .setPlaybackRate({ rate: value })
          .catch((error) => {
            store.getState().emit("error", { sourceEvent: error });
          });
      },
      getCurrentTime: () => {
        return (playbackStateRef.current?.positionMs ?? 0) / 1000;
      },
      getSrc: () => {
        const currentStoreMedia = store.getState().cuedMedia;
        return (
          playbackStateRef.current?.currentItem?.src ??
          (currentStoreMedia?.src
            ? withAssetUrl(currentStoreMedia.src)
            : undefined)
        );
      },
      setMaxLevel: () => {},
      setCurrentAudioTrack: () => {},
      setPlaybackQuality: () => {},
    }),
    // Keep the native provider API stable across track changes. Recreating it
    // causes downstream callbacks to change identity and can re-run the native
    // provider init effect, which reconfigures the CarPlay session.
    [
      consumeSuppressedNativeBridgeCommand,
      runNativeQueueCommand,
      store,
    ],
  );

  const accessToken = getTokenSync() || undefined;
  const userId = getBootstrapData()?.user?.id;

  const baseConfig = useMemo<NativePlaybackConfig>(() => {
    return {
      apiBaseUrl:
        apiClient.defaults.baseURL ||
        (typeof window !== "undefined" ? window.location.origin : ""),
      storageBaseUrl: getStorageBaseUrl(),
      accessToken,
      userId,
    };
  }, [accessToken, userId]);

  const languageOptions = useMemo<NativePlaybackLanguageOptions>(() => {
    const translations = lines ?? {};

    return {
      language: localeCode,
      locale: localeCode,
      siteName: site_name,
      site_name,
      translations,
      i18n: {
        lines: translations,
      },
    };
  }, [lines, localeCode, site_name]);

  const languageSignature = useMemo(
    () => getLanguageSignature(languageOptions),
    [languageOptions],
  );

  const hydrateStoreFromNativeQueue = useCallback(
    (
      snapshot: QueueSnapshot | null,
      state?: Pick<PlaybackState, "currentIndex" | "currentItem"> &
        Partial<Pick<PlaybackState, "durationMs" | "controlsLocked">>,
      options?: {
        syncCurrentMedia?: boolean;
        syncQueueFromNative?: boolean;
      },
    ) => {
      if (!snapshot) {
        logNativePlayback(
          "[NativePlayback][Front] hydrate skipped: empty snapshot",
        );
        return;
      }

      const storeState = store.getState();
      if (!shouldAcceptNativeAudioEvents(storeState)) {
        logNativePlayback(
          "[NativePlayback][Front] hydrate skipped: web provider owns current media",
          {
            cuedMediaId: storeState.cuedMedia?.id,
            cuedMediaPlayerType: storeState.cuedMedia?.playerType,
          },
        );
        return;
      }

      const syncQueueFromNative =
        options?.syncQueueFromNative ?? shouldHydrateQueueFromNative(storeState);
      const existingMediaLookup = createMediaLookup([
        nativeQueueRef.current,
        storeState.originalQueue,
        storeState.shuffledQueue,
      ]);
      const currentMediaFromState = resolveMediaFromPlaybackItem(
        state?.currentItem,
        existingMediaLookup,
      );
      const snapshotQueue = queueSnapshotToMediaQueue(
        snapshot,
        existingMediaLookup,
      );
      const mediaQueue = snapshotQueue.length
        ? snapshotQueue
        : currentMediaFromState
        ? [currentMediaFromState]
        : [];
      const resolvedPlaybackItem =
        state?.currentItem ??
        snapshot.items[state?.currentIndex ?? snapshot.currentIndex] ??
        undefined;
      let currentMedia =
        currentMediaFromState ??
        resolveNativeQueueMedia(mediaQueue, {
          currentIndex: state?.currentIndex ?? snapshot.currentIndex,
          currentItem: undefined,
        }) ??
        mediaQueue[0];
      currentMedia =
        enrichMediaWithPlaybackItem(currentMedia, resolvedPlaybackItem) ??
        currentMedia;
      if (!mediaQueue.length || !currentMedia) {
        logNativePlayback(
          "[NativePlayback][Front] hydrate skipped: no media resolved",
          {
            snapshotItems: snapshot.items.length,
            mediaQueueLength: mediaQueue.length,
            snapshotCurrentIndex: snapshot.currentIndex,
            playbackStateCurrentIndex: state?.currentIndex,
            playbackStateCurrentItemId: state?.currentItem?.id,
          },
        );
        return;
      }

      if (shouldKeepPendingCuedMedia(storeState, currentMedia)) {
        logNativePlayback(
          "[NativePlayback][Front] hydrate skipped stale media during local cue",
          {
            incomingMediaId: currentMedia.id,
            incomingMediaGroupId: currentMedia.groupId,
            pendingMediaId: storeState.cuedMedia?.id,
            pendingMediaGroupId: storeState.cuedMedia?.groupId,
          },
        );
        return;
      }

      const effectiveSnapshot =
        snapshotQueue.length > 0
          ? snapshot
          : createSyntheticQueueSnapshot({
              media: currentMedia,
              snapshot,
              state,
            });
      if (!effectiveSnapshot) {
        return;
      }

      queueSnapshotRef.current = effectiveSnapshot;
      nativeQueueRef.current = mediaQueue;
      lastNativeQueueSignatureRef.current =
        getQueueSnapshotSignature(effectiveSnapshot);
      isHydratingFromNativeRef.current = true;

      logNativePlayback("[NativePlayback][Front] hydrate from native queue", {
        snapshotItems: snapshot.items.length,
        mediaQueueLength: mediaQueue.length,
        snapshotCurrentIndex: snapshot.currentIndex,
        playbackStateCurrentIndex: state?.currentIndex,
        playbackStateCurrentItemId: state?.currentItem?.id,
        resolvedMediaId: currentMedia.id,
        resolvedMediaGroupId: currentMedia.groupId,
        resolvedMediaProvider: currentMedia.provider,
      });

      store.setState({
        playbackEngine: "native",
        providerApi,
        ...(syncQueueFromNative
          ? {
              originalQueue: mediaQueue,
              shuffledQueue: mediaQueue,
              shuffling: effectiveSnapshot.shuffling,
              repeat: fromNativeRepeatMode(effectiveSnapshot.repeatMode),
            }
          : {}),
      });

      if (!options?.syncCurrentMedia) {
        return;
      }

      if (
        storeState.cuedMedia &&
        !isSameMedia(storeState.cuedMedia, currentMedia)
      ) {
        storeState.emit("beforeCued", { previous: storeState.cuedMedia });
      }

      const resolvedDuration = getDurationSeconds(
        currentMedia,
        state as PlaybackState | undefined,
      );

      store.setState({
        cuedMedia: currentMedia,
        posterUrl: currentMedia?.poster,
        providerName: currentMedia?.provider,
        providerReady: Boolean(currentMedia),
        controlsLocked: Boolean(
          currentMedia?.isAd ||
            state?.controlsLocked ||
            effectiveSnapshot.controlsLocked,
        ),
        streamType: currentMedia ? getMediaStreamType(currentMedia) : null,
        showLoader: false,
        mediaDuration: resolvedDuration > 0 ? resolvedDuration : 0,
      });
    },
    [providerApi, store],
  );

  const resolveCurrentMediaFromNativeState = useCallback(
    (
      state: Pick<PlaybackState, "currentIndex" | "currentItem" | "controlsLocked">,
    ) => {
      const nativeQueue = nativeQueueRef.current;
      const currentMediaFromState = resolveMediaFromPlaybackItem(
        state.currentItem,
        createMediaLookup([
          nativeQueue,
          store.getState().originalQueue,
          store.getState().shuffledQueue,
        ]),
      );
      if (currentMediaFromState) {
        return currentMediaFromState;
      }

      if (
        nativeQueue.length &&
        (!store.getState().cuedMedia || !state.controlsLocked)
      ) {
        const media = resolveNativeQueueMedia(nativeQueue, {
          currentIndex: state.currentIndex,
          currentItem: undefined,
        });
        if (media) {
          return media;
        }
      }

      if (state.currentItem) {
        return playbackItemToMediaItem(state.currentItem) ?? undefined;
      }
    },
    [store],
  );

  const syncNativeCurrentMedia = useCallback(
    (state: PlaybackState) => {
      const resolvedCurrentMedia = resolveCurrentMediaFromNativeState(state);
      const storeState = store.getState();
      const currentMedia = shouldPreserveDisplayedAdMedia(
        storeState.cuedMedia,
        resolvedCurrentMedia,
        state,
      )
        ? storeState.cuedMedia
        : resolvedCurrentMedia;

      if (shouldKeepPendingCuedMedia(storeState, currentMedia)) {
        logNativePlayback(
          "[NativePlayback][Front] playbackState skipped stale media during local cue",
          {
            incomingMediaId: currentMedia?.id,
            incomingMediaGroupId: currentMedia?.groupId,
            pendingMediaId: storeState.cuedMedia?.id,
            pendingMediaGroupId: storeState.cuedMedia?.groupId,
          },
        );
        return;
      }

      if (!currentMedia) {
        return currentMedia;
      }

      const resolvedDuration = getDurationSeconds(currentMedia, state);
      const nextStreamType = getMediaStreamType(currentMedia);
      const nextControlsLocked = Boolean(
        currentMedia.isAd || state.controlsLocked,
      );
      const sameMedia =
        !!storeState.cuedMedia &&
        isSameMedia(currentMedia, storeState.cuedMedia);

      if (sameMedia) {
        const shouldRefreshSyncedFields =
          storeState.posterUrl !== currentMedia.poster ||
          storeState.providerName !== currentMedia.provider ||
          storeState.streamType !== nextStreamType ||
          storeState.controlsLocked !== nextControlsLocked ||
          (resolvedDuration > 0 && storeState.mediaDuration !== resolvedDuration);

        if (shouldRefreshSyncedFields) {
          store.setState({
            cuedMedia: currentMedia,
            posterUrl: currentMedia.poster,
            providerName: currentMedia.provider,
            playbackEngine: "native",
            providerApi,
            providerReady: true,
            controlsLocked: nextControlsLocked,
            streamType: nextStreamType,
            showLoader: false,
            mediaDuration:
              resolvedDuration > 0 ? resolvedDuration : storeState.mediaDuration,
          });
        }
        return currentMedia;
      }

      storeState.emit("beforeCued", { previous: storeState.cuedMedia });
      store.setState({
        cuedMedia: currentMedia,
        posterUrl: currentMedia.poster,
        providerName: currentMedia.provider,
        playbackEngine: "native",
        providerApi,
        providerReady: true,
        controlsLocked: nextControlsLocked,
        streamType: nextStreamType,
        showLoader: false,
        mediaDuration: resolvedDuration > 0 ? resolvedDuration : 0,
      });

      return currentMedia;
    },
    [providerApi, resolveCurrentMediaFromNativeState, store],
  );

  const emitCueLifecycle = useCallback(
    (media?: MediaItem, state?: PlaybackState) => {
      if (!media) return;

      const signature = getCueSignature(media);
      if (lastCueSignatureRef.current === signature) {
        return;
      }
      lastCueSignatureRef.current = signature;

      const emit = store.getState().emit;
      emit("cued");
      emit("playbackRates", { rates: defaultPlaybackRates });
      emit("audioTracks", { tracks: [] });
      emit("playbackQualities", { qualities: ["auto"] });
      emit("playbackQualityChange", { quality: "auto" });

      const streamType = getMediaStreamType(media);
      if (streamType) {
        emit("streamTypeChange", { streamType });
      }

      const duration = getDurationSeconds(media, state);
      if (duration > 0) {
        emit("durationChange", { duration });
      }

      emit("providerReady", { el: providerElementRef.current! });
    },
    [store],
  );

  const applyPlaybackState = useCallback(
    (nextState: PlaybackState) => {
      const currentStoreState = store.getState();
      if (!shouldAcceptNativeAudioEvents(currentStoreState)) {
        logNativePlayback(
          "[NativePlayback][Front] playbackState ignored: web provider owns current media",
          {
            currentItemId: nextState.currentItem?.id,
            cuedMediaId: currentStoreState.cuedMedia?.id,
            cuedMediaPlayerType: currentStoreState.cuedMedia?.playerType,
          },
        );
        return false;
      }

      const previousState = playbackStateRef.current;
      const normalizedState = normalizePlaybackStateWithPrevious(
        nextState,
        previousState,
      );

      if (
        isFrontendQueueSyncPending(
          pendingFrontendQueueSignatureRef.current,
          queueSyncPromiseRef.current,
        )
      ) {
        logNativePlayback(
          "[NativePlayback][Front] playbackState ignored: frontend queue sync pending",
          {
            currentItemId: normalizedState.currentItem?.id,
            currentIndex: normalizedState.currentIndex,
            isPlaying: normalizedState.isPlaying,
            positionMs: normalizedState.positionMs,
          },
        );
        return false;
      }

      playbackStateRef.current = normalizedState;

      const mergedNativeSnapshot = mergeQueueSnapshotWithPlaybackState(
        queueSnapshotRef.current,
        normalizedState,
      );
      if (mergedNativeSnapshot) {
        queueSnapshotRef.current = mergedNativeSnapshot;
        lastNativeQueueSignatureRef.current =
          getQueueSnapshotSignature(mergedNativeSnapshot);
      }

      const syncedMedia = syncNativeCurrentMedia(normalizedState);
      if (syncedMedia) {
        emitCueLifecycle(syncedMedia, normalizedState);
      }

      logNativePlayback("[NativePlayback][Front] apply playbackState", {
        previousItemId: previousState?.currentItem?.id,
        nextItemId: normalizedState.currentItem?.id,
        currentIndex: normalizedState.currentIndex,
        isPlaying: normalizedState.isPlaying,
        isBuffering: normalizedState.isBuffering,
        positionMs: normalizedState.positionMs,
        durationMs: normalizedState.durationMs,
        syncedMediaId: syncedMedia?.id,
        syncedMediaGroupId: syncedMedia?.groupId,
      });

      const storeState = store.getState();
      store.setState({
        isPlaying: normalizedState.isPlaying,
        isBuffering: normalizedState.isBuffering,
        playbackEngine: "native",
        controlsLocked:
          normalizedState.controlsLocked ?? storeState.controlsLocked,
        playbackStarted: storeState.playbackStarted || normalizedState.isPlaying,
      });

      const emit = store.getState().emit;
      emit("progress", { currentTime: normalizedState.positionMs / 1000 });

      if (
        !previousState ||
        previousState.durationMs !== normalizedState.durationMs
      ) {
        const duration = normalizedState.durationMs / 1000;
        if (duration > 0) {
          emit("durationChange", { duration });
        }
      }

      if (!previousState || previousState.rate !== normalizedState.rate) {
        emit("playbackRateChange", { rate: normalizedState.rate });
      }

      if (
        !previousState ||
        previousState.isBuffering !== normalizedState.isBuffering
      ) {
        emit("buffering", { isBuffering: normalizedState.isBuffering });
      }

      if (
        !previousState ||
        previousState.isPlaying !== normalizedState.isPlaying
      ) {
        if (normalizedState.isPlaying) {
          emit("play");
        } else {
          emit("pause");
        }
      }

      const didReachPlaybackEnd =
        !!previousState?.isPlaying &&
        !normalizedState.isPlaying &&
        !normalizedState.isBuffering &&
        normalizedState.durationMs > 0 &&
        normalizedState.positionMs >= normalizedState.durationMs - 250 &&
        previousState.currentIndex === normalizedState.currentIndex;

      if (!didReachPlaybackEnd) {
        pendingPlaybackEndCheckRef.current = null;
        return true;
      }

      const playbackEndCheckToken = Symbol("nativePlaybackEndCheck");
      pendingPlaybackEndCheckRef.current = playbackEndCheckToken;

      void (async () => {
        await new Promise((resolve) => {
          setTimeout(resolve, NATIVE_PLAYBACK_END_SETTLE_DELAY_MS);
        });
        if (pendingPlaybackEndCheckRef.current !== playbackEndCheckToken) {
          return;
        }

        const latestSnapshot = await nativePlaybackBridge.getState();
        if (pendingPlaybackEndCheckRef.current !== playbackEndCheckToken) {
          return;
        }

        if (
          didNativePlaybackAdvancePastEndedState(
            normalizedState,
            latestSnapshot.state,
          )
        ) {
          logNativePlayback(
            "[NativePlayback][Front] playbackEnd skipped: native advanced to a new state",
            {
              endedItemId: normalizedState.currentItem?.id,
              endedCurrentIndex: normalizedState.currentIndex,
              latestItemId: latestSnapshot.state.currentItem?.id,
              latestCurrentIndex: latestSnapshot.state.currentIndex,
              latestIsPlaying: latestSnapshot.state.isPlaying,
              latestIsBuffering: latestSnapshot.state.isBuffering,
              latestPositionMs: latestSnapshot.state.positionMs,
            },
          );
          return;
        }

        emit("playbackEnd");
      })().catch((error) => {
        store.getState().emit("error", { sourceEvent: error });
      });

      return true;
    },
    [emitCueLifecycle, store, syncNativeCurrentMedia],
  );

  const restoreFrontendQueueToNative = useCallback(async () => {
    const storeState = store.getState();
    const mirroredSnapshot = createMirroredNativeQueueSnapshot({
      shuffledQueue: storeState.shuffledQueue,
      cuedMedia: storeState.cuedMedia,
      shuffling: storeState.shuffling,
      repeat: storeState.repeat,
      controlsLocked: storeState.controlsLocked,
    });
    if (!mirroredSnapshot) {
      return;
    }

    const nextQueueSignature = getQueueSnapshotSignature(
      mirroredSnapshot.snapshot,
    );
    pendingFrontendQueueSignatureRef.current = nextQueueSignature;

    await runNativeQueueCommand(
      () => nativePlaybackBridge.setQueue(mirroredSnapshot.snapshot),
      { authority: "frontend" },
    );

    queueSnapshotRef.current = mirroredSnapshot.snapshot;
    nativeQueueRef.current = mirroredSnapshot.mediaQueue;
    lastNativeQueueSignatureRef.current = nextQueueSignature;
    if (pendingFrontendQueueSignatureRef.current === nextQueueSignature) {
      pendingFrontendQueueSignatureRef.current = null;
    }
  }, [runNativeQueueCommand, store]);

  const restoreNativeTransportState = useCallback(
    async (previousState?: PlaybackState | null) => {
      if (previousState?.isPlaying) {
        await nativePlaybackBridge.play();
      } else {
        await nativePlaybackBridge.pause();
      }
    },
    [],
  );

  const handleRemoteCommand = useCallback(
    async (event: RemoteCommandReceivedEvent) => {
      const state = store.getState();
      const nativePlaybackOwnsCommand = state.playbackEngine === "native";
      if (nativePlaybackOwnsCommand) {
        const previousPlaybackState = playbackStateRef.current;
        const previousPositionMs =
          previousPlaybackState?.positionMs ??
          Math.max(0, Math.round(state.getCurrentTime() * 1000));

        const revertBlockedNativeCommand = async () => {
          switch (event.command) {
            case "play":
            case "pause":
            case "stop":
              await restoreNativeTransportState(previousPlaybackState);
              break;
            case "seekTo":
              await nativePlaybackBridge.seekTo({ positionMs: previousPositionMs });
              break;
            case "next":
            case "previous":
            case "skipToIndex":
              await restoreFrontendQueueToNative();
              await restoreNativeTransportState(previousPlaybackState);
              break;
          }
        };

        if (event.command === "setRepeatMode") {
          const { userAccess } = getUserRolesAccess();
          const canUseRepeatControl =
            userAccess.medias.play.base &&
            parsePlaybackRestrictionBool(userAccess.medias.play.canRepeat);
          if (state.controlsLocked || !canUseRepeatControl) {
            await nativePlaybackBridge.setRepeatMode({
              mode: toNativeRepeatMode(state.repeat),
            });
            return;
          }

          syncRepeatModeFromNative(
            store.getState,
            event.payload?.mode as NativeRepeatMode | undefined,
          );
          return;
        }

        if (event.command === "setShuffle") {
          const { userAccess } = getUserRolesAccess();
          const canUseShuffleControl =
            userAccess.medias.play.base &&
            parsePlaybackRestrictionBool(userAccess.medias.play.canShuffle);
          if (state.controlsLocked || !canUseShuffleControl) {
            await nativePlaybackBridge.setShuffle({
              enabled: state.shuffling,
            });
            return;
          }

          syncShuffleFromNative(
            store.getState,
            Boolean(event.payload?.enabled),
          );
          return;
        }

        if (state.controlsLocked) {
          logNativePlayback(
            "[NativePlayback][Front] remote command reverted: controls locked",
            {
              command: event.command,
            },
          );
          await revertBlockedNativeCommand();
          return;
        }

        if (event.command === "play") {
          const suppressedCommand: SuppressedNativeBridgeCommand = {
            command: "play",
            consumed: false,
          };
          suppressNativeBridgeCommand(suppressedCommand);
          await state.play(undefined, true);
          if (!suppressedCommand.consumed) {
            suppressedNativeBridgeCommandRef.current = null;
            await revertBlockedNativeCommand();
          }
          return;
        }

        if (event.command === "pause") {
          return;
        }

        if (event.command === "stop") {
          return;
        }

        if (event.command === "next") {
          const suppressedCommand: SuppressedNativeBridgeCommand = {
            command: "next",
            consumed: false,
          };
          suppressNativeBridgeCommand(suppressedCommand);
          await state.playNext(true, true);
          if (!suppressedCommand.consumed) {
            suppressedNativeBridgeCommandRef.current = null;
            await revertBlockedNativeCommand();
          }
          return;
        }

        if (event.command === "previous") {
          const suppressedCommand: SuppressedNativeBridgeCommand = {
            command: "previous",
            consumed: false,
          };
          suppressNativeBridgeCommand(suppressedCommand);
          await state.playPrevious(true, true);
          if (!suppressedCommand.consumed) {
            suppressedNativeBridgeCommandRef.current = null;
            await revertBlockedNativeCommand();
          }
          return;
        }

        if (event.command === "seekTo") {
          const positionMs = Math.max(
            0,
            Math.round(Number(event.payload?.positionMs ?? 0)),
          );
          const suppressedCommand: SuppressedNativeBridgeCommand = {
            command: "seekTo",
            positionMs,
            consumed: false,
          };
          suppressNativeBridgeCommand(suppressedCommand);
          state.seek(positionMs / 1000, true, true);
          if (!suppressedCommand.consumed) {
            suppressedNativeBridgeCommandRef.current = null;
            await revertBlockedNativeCommand();
          }
          return;
        }

        if (event.command === "skipToIndex") {
          const nativeIndex = Number(event.payload?.index);
          const targetIndex = resolveStoreQueueIndex(
            state.shuffledQueue,
            nativeQueueRef.current,
            nativeIndex,
          );

          if (!Number.isInteger(targetIndex) || targetIndex < 0) {
            await revertBlockedNativeCommand();
            return;
          }

          const suppressedCommand: SuppressedNativeBridgeCommand = {
            command: "skipToIndex",
            index: targetIndex,
            consumed: false,
          };
          suppressNativeBridgeCommand(suppressedCommand);
          await state.playQueueItemAtIndex(targetIndex, true, true);
          if (!suppressedCommand.consumed) {
            suppressedNativeBridgeCommandRef.current = null;
            await revertBlockedNativeCommand();
          }
          return;
        }

        logNativePlayback(
          "[NativePlayback][Front] remote command ignored: native playback already applied command",
          {
            command: event.command,
            nativeQueueAuthoritative: isNativeQueueAuthoritative(state),
          },
        );
        return;
      }
      if (state.controlsLocked) {
        logNativePlayback(
          "[NativePlayback][Front] remote command ignored: controls locked",
          {
            command: event.command,
          },
        );
        return;
      }
      switch (event.command) {
        case "play":
          void state.play(undefined, true);
          break;
        case "pause":
          state.pause(true);
          break;
        case "stop":
          state.stop(true);
          break;
        case "next":
          void state.playNext(true, true);
          break;
        case "previous":
          void state.playPrevious(true, true);
          break;
        case "seekTo": {
          const positionMs = Number(event.payload?.positionMs ?? 0);
          state.seek(positionMs / 1000, true, true);
          break;
        }
        case "skipToIndex": {
          const nativeIndex = Number(event.payload?.index);
          const targetIndex = resolveStoreQueueIndex(
            store.getState().shuffledQueue,
            nativeQueueRef.current,
            nativeIndex,
          );
          if (Number.isInteger(targetIndex) && targetIndex >= 0) {
            void state.playQueueItemAtIndex(targetIndex, true, true);
          }
          break;
        }
        case "setRepeatMode":
          syncRepeatModeFromNative(
            store.getState,
            event.payload?.mode as NativeRepeatMode | undefined,
          );
          break;
        case "setShuffle":
          syncShuffleFromNative(
            store.getState,
            Boolean(event.payload?.enabled),
          );
          break;
      }
    },
    [
      restoreFrontendQueueToNative,
      restoreNativeTransportState,
      store,
      suppressNativeBridgeCommand,
    ],
  );

  const syncLanguage = useCallback(async (force = false) => {
    const nextLanguageOptions = languageOptionsRef.current;
    if (!nextLanguageOptions) {
      return;
    }

    const nextLanguageSignature = getLanguageSignature(nextLanguageOptions);
    if (!force && lastLanguageSignatureRef.current === nextLanguageSignature) {
      return;
    }

    logNativePlayback("[NativePlayback] sync language", {
      language: nextLanguageOptions.language,
      siteName: nextLanguageOptions.siteName ?? nextLanguageOptions.site_name,
      translationsCount: Object.keys(nextLanguageOptions.translations ?? {})
        .length,
    });

    await nativePlaybackBridge.setLanguage(nextLanguageOptions);
    lastLanguageSignatureRef.current = nextLanguageSignature;
  }, []);

  const syncMirroredQueueToNative = useCallback(async () => {
    const storeState = store.getState();
    if (!shouldAcceptNativeAudioEvents(storeState)) {
      return;
    }

    if (
      nativeQueueAuthorityRef.current === "native" ||
      pendingNativeQueueReconcileRef.current
    ) {
      return;
    }

    const mirroredSnapshot = createMirroredNativeQueueSnapshot({
      shuffledQueue: storeState.shuffledQueue,
      cuedMedia: storeState.cuedMedia,
      shuffling: storeState.shuffling,
      repeat: storeState.repeat,
      controlsLocked: storeState.controlsLocked,
    });
    if (!mirroredSnapshot) {
      return;
    }

    const nextQueueSignature = getQueueSnapshotSignature(
      mirroredSnapshot.snapshot,
    );
    const nextQueueStructureSignature = getQueueStructureSignature(
      mirroredSnapshot.snapshot,
    );

    if (nextQueueSignature === lastNativeQueueSignatureRef.current) {
      lastFrontendMirroredQueueStructureRef.current =
        nextQueueStructureSignature;
      return;
    }

    lastFrontendMirroredQueueStructureRef.current = nextQueueStructureSignature;
    pendingFrontendQueueSignatureRef.current = nextQueueSignature;

    await runNativeQueueCommand(
      () => nativePlaybackBridge.setQueue(mirroredSnapshot.snapshot),
      { authority: "frontend" },
    );

    queueSnapshotRef.current = mirroredSnapshot.snapshot;
    nativeQueueRef.current = mirroredSnapshot.mediaQueue;
    lastNativeQueueSignatureRef.current = nextQueueSignature;
    if (pendingFrontendQueueSignatureRef.current === nextQueueSignature) {
      pendingFrontendQueueSignatureRef.current = null;
    }
  }, [runNativeQueueCommand, store]);

  const getNativeQueueSyncDecision = useCallback(
    (
      snapshot: QueueSnapshot,
      storeState: Pick<PlayerStoreState, "shuffledQueue" | "cuedMedia">,
    ) => {
      const incomingStructureSignature = getQueueStructureSignature(snapshot);
      const structureDiffersFromFrontendMirror =
        Boolean(lastFrontendMirroredQueueStructureRef.current) &&
        incomingStructureSignature !==
          lastFrontendMirroredQueueStructureRef.current;
      const shouldPromoteNativeQueueAuthority =
        nativeQueueAuthorityRef.current !== "frontend" ||
        structureDiffersFromFrontendMirror;

      return {
        incomingStructureSignature,
        shouldPromoteNativeQueueAuthority,
        shouldSyncQueueFromNative:
          shouldPromoteNativeQueueAuthority ||
          shouldHydrateQueueFromNative(storeState) ||
          structureDiffersFromFrontendMirror,
      };
    },
    [],
  );

  useEffect(() => {
    if (cuedMedia && !canUseNativeAudioPlaybackForMedia(cuedMedia)) {
      return;
    }

    logNativePlayback("[NativePlayback][Front] bind native providerApi", {
      providerName,
      cuedMediaId: cuedMedia?.id,
      cuedMediaPlayerType: cuedMedia?.playerType,
    });
    store.setState({ playbackEngine: "native", providerApi });
  }, [cuedMedia, providerApi, providerName, store]);

  useEffect(() => {
    if (!cuedMedia || !canUseNativeAudioPlaybackForMedia(cuedMedia)) {
      return;
    }

    let cancelled = false;

    void (async () => {
      try {
        await queueSyncPromiseRef.current;
        if (cancelled) return;
        await syncMirroredQueueToNative();
      } catch (error) {
        if (!cancelled) {
          store.getState().emit("error", { sourceEvent: error });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    cuedMedia,
    shuffledQueue,
    shuffling,
    repeat,
    controlsLocked,
    store,
    syncMirroredQueueToNative,
  ]);

  useEffect(() => {
    return () => {
      pendingNativeQueueReconcileRef.current = null;
      nativeQueueAuthorityRef.current = "unknown";
    };
  }, []);

  useEffect(() => {
    languageOptionsRef.current = languageOptions;
  }, [languageOptions]);

  useEffect(() => {
    let cancelled = false;
    const removers: Array<() => void> = [];
    let remoteCommandSyncTimeoutId: ReturnType<typeof setTimeout> | null = null;
    const nativeSubscriptionRetryTimeoutIds: Array<
      ReturnType<typeof setTimeout>
    > = [];

    async function init() {
      function scheduleNativePlaybackSubscriptionRetry(reason: string) {
        if (cancelled || nativeEventListenersBoundRef.current) {
          return;
        }

        if (scheduledNativeSubscriptionRetryRef.current) {
          return;
        }

        const retryPlanToken = Symbol("nativePlaybackSubscriptionRetry");
        scheduledNativeSubscriptionRetryRef.current = retryPlanToken;
        const retryDelaysMs = [250, 1000, 3000, 10000];

        retryDelaysMs.forEach((delay, index) => {
          const timeoutId = setTimeout(() => {
            if (scheduledNativeSubscriptionRetryRef.current !== retryPlanToken) {
              return;
            }

            if (!cancelled && !nativeEventListenersBoundRef.current) {
              logNativePlayback(
                "[NativePlayback][Front] retry native listener bind",
                {
                  reason,
                  delay,
                },
              );
              triggerNativePlaybackSubscription(`${reason}:retry:${delay}`, {
                forceRebind: true,
                forceConfigure: true,
              });
            }

            if (
              index === retryDelaysMs.length - 1 &&
              scheduledNativeSubscriptionRetryRef.current === retryPlanToken
            ) {
              scheduledNativeSubscriptionRetryRef.current = null;
            }
          }, delay);
          nativeSubscriptionRetryTimeoutIds.push(timeoutId);
        });
      }

      const sanitizeInitialNativeQueueSnapshot = async (
        snapshot: NativePlaybackStateResponse,
        reason: string,
      ) => {
        if (hasProcessedInitialNativeQueueRef.current) {
          return snapshot;
        }
        hasProcessedInitialNativeQueueRef.current = true;

        const sanitizedSnapshot = removeAdItemsFromNativeSnapshot(snapshot);
        if (!sanitizedSnapshot) {
          return snapshot;
        }

        logNativePlayback(
          "[NativePlayback][Front] remove ad items from initial native queue snapshot",
          {
            reason,
            originalItems: snapshot.queue.items.length,
            sanitizedItems: sanitizedSnapshot.queue.items.length,
            originalCurrentItemId: snapshot.state.currentItem?.id,
            sanitizedCurrentItemId: sanitizedSnapshot.state.currentItem?.id,
            originalCurrentIndex: snapshot.state.currentIndex,
            sanitizedCurrentIndex: sanitizedSnapshot.state.currentIndex,
          },
        );

        await nativePlaybackBridge.setQueue(sanitizedSnapshot.queue);
        return sanitizedSnapshot;
      };

      const schedulePostSyncRefresh = (reason: string) => {
        const syncPromise = queueSyncPromiseRef.current;
        if (!syncPromise || scheduledPostSyncRefreshRef.current) {
          return;
        }

        const refreshToken = Symbol("nativePostSyncRefresh");
        scheduledPostSyncRefreshRef.current = refreshToken;

        void syncPromise.finally(() => {
          if (scheduledPostSyncRefreshRef.current !== refreshToken) {
            return;
          }

          if (
            queueSyncPromiseRef.current &&
            queueSyncPromiseRef.current !== syncPromise
          ) {
            scheduledPostSyncRefreshRef.current = null;
            schedulePostSyncRefresh(`${reason}:rescheduled`);
            return;
          }

          scheduledPostSyncRefreshRef.current = null;
          if (cancelled) return;

          void nativePlaybackBridge.getState().then((snapshot) => {
            if (cancelled) return;
            logNativePlayback(
              "[NativePlayback][Front] post-sync native refresh result",
              {
                reason,
                queueItems: snapshot.queue.items.length,
                currentItemId: snapshot.state.currentItem?.id,
                currentIndex: snapshot.state.currentIndex,
                isPlaying: snapshot.state.isPlaying,
                positionMs: snapshot.state.positionMs,
              },
            );
            hydrateStoreFromNativeQueue(snapshot.queue, snapshot.state);
            applyPlaybackState(snapshot.state);
          });
        });
      };

      const scheduleRemoteCommandStateSync = (
        event: RemoteCommandReceivedEvent,
      ) => {
        if (event.command === "setRepeatMode" || event.command === "setShuffle") {
          return;
        }

        if (remoteCommandSyncTimeoutId) {
          clearTimeout(remoteCommandSyncTimeoutId);
        }

        remoteCommandSyncTimeoutId = setTimeout(() => {
          remoteCommandSyncTimeoutId = null;

          if (cancelled) return;

          if (queueSyncPromiseRef.current) {
            schedulePostSyncRefresh(`remoteCommandReceived:${event.command}`);
            return;
          }

          void syncStoreFromNativeState(`remoteCommandReceived:${event.command}`, {
            promoteUsableSnapshotToNative: true,
          }).catch((error) => {
            if (!cancelled) {
              store.getState().emit("error", { sourceEvent: error });
            }
          });
        }, NATIVE_REMOTE_COMMAND_SYNC_DELAY_MS);
      };

      const configureNativeSession = async (
        reason: string,
        force = false,
      ) => {
        if (!force && hasConfiguredSessionRef.current) {
          return;
        }

        logNativePlayback("[NativePlayback][Front] configure native session", {
          reason,
          force,
          language: languageOptions.language,
        });

        await nativePlaybackBridge.configure({
          ...baseConfig,
          language: languageOptions.language,
        });
        hasConfiguredSessionRef.current = true;
        await syncLanguage(true);
      };

      const syncStoreFromNativeState = async (
        reason: string,
        options?: {
          promoteUsableSnapshotToNative?: boolean;
        },
      ) => {
        const initialSnapshot = await nativePlaybackBridge.getState();
        if (cancelled) return;
        if (!shouldAcceptNativeAudioEvents(store.getState())) {
          return;
        }
        const snapshot = await sanitizeInitialNativeQueueSnapshot(
          initialSnapshot,
          reason,
        );
        if (cancelled) return;
        hasBootstrappedNativeStateRef.current = true;
        const storeState = store.getState();
        const hasUsableSnapshot = hasUsableNativePlaybackSnapshot(snapshot);
        const shouldPromoteUsableSnapshotToNative =
          hasUsableSnapshot &&
          (options?.promoteUsableSnapshotToNative ??
            nativeQueueAuthorityRef.current !== "frontend");
        const {
          shouldPromoteNativeQueueAuthority,
          shouldSyncQueueFromNative,
        } = getNativeQueueSyncDecision(snapshot.queue, storeState);
        const shouldPromoteSnapshotToNative =
          hasUsableSnapshot &&
          (
            shouldPromoteUsableSnapshotToNative ||
            shouldPromoteNativeQueueAuthority
          );
        if (shouldPromoteSnapshotToNative) {
          nativeQueueAuthorityRef.current = "native";
        }
        logNativePlayback(`[NativePlayback][Front] ${reason} getState result`, {
          queueItems: snapshot.queue.items.length,
          currentItemId: snapshot.state.currentItem?.id,
          currentIndex: snapshot.state.currentIndex,
          isPlaying: snapshot.state.isPlaying,
          positionMs: snapshot.state.positionMs,
        });
        if (
          pendingFrontendQueueSignatureRef.current &&
          pendingFrontendQueueSignatureRef.current !==
            getQueueSnapshotSignature(snapshot.queue)
        ) {
          if (queueSyncPromiseRef.current) {
            logNativePlayback(
              `[NativePlayback][Front] ${reason} ignored: frontend sync pending`,
              {
                incomingSignature: getQueueSnapshotSignature(snapshot.queue),
                pendingFrontendQueueSignature:
                  pendingFrontendQueueSignatureRef.current,
              },
            );
            schedulePostSyncRefresh(reason);
            return;
          }
          logNativePlayback(
            `[NativePlayback][Front] ${reason} clears stale pending frontend signature`,
            {
              incomingSignature: getQueueSnapshotSignature(snapshot.queue),
              pendingFrontendQueueSignature:
                pendingFrontendQueueSignatureRef.current,
            },
          );
          pendingFrontendQueueSignatureRef.current = null;
        }

        hydrateStoreFromNativeQueue(snapshot.queue, snapshot.state, {
          syncQueueFromNative:
            shouldPromoteSnapshotToNative || shouldSyncQueueFromNative,
          syncCurrentMedia: shouldPromoteSnapshotToNative,
        });
        applyPlaybackState(snapshot.state);
      };

      let nativePlaybackListenerRemovers: Array<() => void> = [];
      let nativePlaybackSubscriptionPromise: Promise<boolean> | null = null;

      const disposeNativePlaybackListeners = () => {
        nativePlaybackListenerRemovers.forEach((remove) => remove());
        nativePlaybackListenerRemovers = [];
        nativeEventListenersBoundRef.current = false;
      };

      const registerNativePlaybackListeners = async (
        reason: string,
      ): Promise<boolean> => {
        disposeNativePlaybackListeners();
        await nativePlaybackBridge.removeAllListeners();
        if (cancelled) return false;

        const [
          removePlaybackState,
          removeQueueChanged,
          removeRemoteCommand,
          removeCarConnectionChanged,
          removeLimitReached,
        ] = await Promise.all([
          nativePlaybackBridge.addPlaybackStateChangedListener((event) => {
            if (!shouldAcceptNativeAudioEvents(store.getState())) {
              logNativePlayback(
                "[NativePlayback][Front] event playbackStateChanged ignored: web provider owns current media",
                {
                  currentItemId: event.state.currentItem?.id,
                  cuedMediaId: store.getState().cuedMedia?.id,
                },
              );
              return;
            }

            const previousPlaybackState = playbackStateRef.current;
            const normalizedEventState = normalizePlaybackStateWithPrevious(
              event.state,
              previousPlaybackState,
            );
            hasBootstrappedNativeStateRef.current = true;
            logNativePlayback(
              "[NativePlayback][Front] event playbackStateChanged",
              {
                previousItemId: previousPlaybackState?.currentItem?.id,
                nextItemId: normalizedEventState.currentItem?.id,
                currentIndex: normalizedEventState.currentIndex,
                isPlaying: normalizedEventState.isPlaying,
                isBuffering: normalizedEventState.isBuffering,
                positionMs: normalizedEventState.positionMs,
                durationMs: normalizedEventState.durationMs,
              },
            );
            const playbackStateApplied = applyPlaybackState(normalizedEventState);
            if (!playbackStateApplied) {
              schedulePostSyncRefresh("playbackStateChanged");
              return;
            }
            const shouldRehydrate = shouldRehydrateFromPlaybackStateChange(
              previousPlaybackState,
              normalizedEventState,
              store.getState().cuedMedia,
              queueSnapshotRef.current,
            );
            logNativePlayback(
              "[NativePlayback][Front] playbackState rehydrate decision",
              {
                shouldRehydrate,
                storeCuedMediaId: store.getState().cuedMedia?.id,
                queueItems: queueSnapshotRef.current?.items.length ?? 0,
              },
            );
            if (shouldRehydrate) {
              const reconcileToken = Symbol("nativeQueueReconcile");
              pendingNativeQueueReconcileRef.current = reconcileToken;
              void nativePlaybackBridge
                .getState()
                .then(async (snapshot) => {
                  if (cancelled) return;
                  if (!shouldAcceptNativeAudioEvents(store.getState())) {
                    return;
                  }
                  const sanitizedSnapshot =
                    await sanitizeInitialNativeQueueSnapshot(
                      snapshot,
                      "playbackStateChanged:getState",
                    );
                  if (cancelled) return;
                  const currentStoreState = store.getState();
                  const {
                    shouldPromoteNativeQueueAuthority,
                    shouldSyncQueueFromNative,
                  } = getNativeQueueSyncDecision(
                    sanitizedSnapshot.queue,
                    currentStoreState,
                  );
                  if (shouldPromoteNativeQueueAuthority) {
                    nativeQueueAuthorityRef.current = "native";
                  }
                  logNativePlayback(
                    "[NativePlayback][Front] playbackState forced getState result",
                    {
                      queueItems: sanitizedSnapshot.queue.items.length,
                      currentItemId: sanitizedSnapshot.state.currentItem?.id,
                      currentIndex: sanitizedSnapshot.state.currentIndex,
                      isPlaying: sanitizedSnapshot.state.isPlaying,
                      positionMs: sanitizedSnapshot.state.positionMs,
                      shouldSyncQueueFromNative,
                    },
                  );
                  hydrateStoreFromNativeQueue(
                    sanitizedSnapshot.queue,
                    sanitizedSnapshot.state,
                    {
                      syncQueueFromNative: shouldSyncQueueFromNative,
                      syncCurrentMedia: shouldSyncQueueFromNative,
                    },
                  );
                  applyPlaybackState(sanitizedSnapshot.state);
                })
                .finally(() => {
                  if (
                    pendingNativeQueueReconcileRef.current === reconcileToken
                  ) {
                    pendingNativeQueueReconcileRef.current = null;
                  }
                });
            }
          }),
          nativePlaybackBridge.addQueueChangedListener((event) => {
            const storeState = store.getState();
            if (!shouldAcceptNativeAudioEvents(storeState)) {
              logNativePlayback(
                "[NativePlayback][Front] event queueChanged ignored: web provider owns current media",
                {
                  items: event.queue.items.length,
                  cuedMediaId: storeState.cuedMedia?.id,
                },
              );
              return;
            }

            hasBootstrappedNativeStateRef.current = true;
            const incomingSignature = getQueueSnapshotSignature(event.queue);
            const {
              incomingStructureSignature,
              shouldPromoteNativeQueueAuthority,
              shouldSyncQueueFromNative,
            } = getNativeQueueSyncDecision(event.queue, storeState);
            if (shouldPromoteNativeQueueAuthority) {
              nativeQueueAuthorityRef.current = "native";
            }
            const queueEventState = createQueueEventPlaybackState(
              event.queue,
              playbackStateRef.current,
            );
            logNativePlayback("[NativePlayback][Front] event queueChanged", {
              items: event.queue.items.length,
              currentIndex: event.queue.currentIndex,
              shuffling: event.queue.shuffling,
              repeatMode: event.queue.repeatMode,
              incomingSignature,
              incomingStructureSignature,
              pendingFrontendQueueSignature:
                pendingFrontendQueueSignatureRef.current,
              hasQueueSyncPromise: Boolean(queueSyncPromiseRef.current),
              shouldSyncQueueFromNative,
            });
            if (
              pendingFrontendQueueSignatureRef.current &&
              pendingFrontendQueueSignatureRef.current !== incomingSignature
            ) {
              if (queueSyncPromiseRef.current) {
                logNativePlayback(
                  "[NativePlayback][Front] queueChanged ignored: waiting for frontend sync to finish",
                  {
                    incomingSignature,
                    pendingFrontendQueueSignature:
                      pendingFrontendQueueSignatureRef.current,
                  },
                );
                schedulePostSyncRefresh("queueChanged");
                return;
              }
              logNativePlayback(
                "[NativePlayback][Front] queueChanged clears stale pending frontend signature",
                {
                  incomingSignature,
                  pendingFrontendQueueSignature:
                    pendingFrontendQueueSignatureRef.current,
                },
              );
              pendingFrontendQueueSignatureRef.current = null;
            }
            if (pendingFrontendQueueSignatureRef.current === incomingSignature) {
              pendingFrontendQueueSignatureRef.current = null;
            }
            void (async () => {
              const sanitizedEventSnapshot =
                await sanitizeInitialNativeQueueSnapshot(
                  {
                    queue: event.queue,
                    state: queueEventState,
                  },
                  "queueChanged",
                );
              if (cancelled) return;
              if (!shouldAcceptNativeAudioEvents(store.getState())) {
                return;
              }
              hydrateStoreFromNativeQueue(
                sanitizedEventSnapshot.queue,
                sanitizedEventSnapshot.state,
                {
                  syncQueueFromNative: shouldSyncQueueFromNative,
                },
              );
              const snapshot = await nativePlaybackBridge.getState();
              if (cancelled) return;
              if (!shouldAcceptNativeAudioEvents(store.getState())) {
                return;
              }
              const sanitizedSnapshot = await sanitizeInitialNativeQueueSnapshot(
                snapshot,
                "queueChanged:getState",
              );
              if (cancelled) return;
              logNativePlayback(
                "[NativePlayback][Front] queueChanged follow-up getState result",
                {
                  queueItems: sanitizedSnapshot.queue.items.length,
                  currentItemId: sanitizedSnapshot.state.currentItem?.id,
                  currentIndex: sanitizedSnapshot.state.currentIndex,
                  isPlaying: sanitizedSnapshot.state.isPlaying,
                  positionMs: sanitizedSnapshot.state.positionMs,
                },
              );
              hydrateStoreFromNativeQueue(
                sanitizedSnapshot.queue,
                sanitizedSnapshot.state,
                {
                  syncQueueFromNative: shouldSyncQueueFromNative,
                },
              );
              applyPlaybackState(sanitizedSnapshot.state);
            })();
          }),
          nativePlaybackBridge.addRemoteCommandListener((event) => {
            const nativePlaybackOwnsCommand =
              store.getState().playbackEngine === "native";
            void handleRemoteCommand(event).catch((error) => {
              if (!cancelled) {
                store.getState().emit("error", { sourceEvent: error });
              }
            });

            if (nativePlaybackOwnsCommand) {
              const hasKnownNativeState = Boolean(
                playbackStateRef.current?.currentItem ||
                  queueSnapshotRef.current?.items.length,
              );
              if (!hasKnownNativeState) {
                triggerNativePlaybackSubscription("remoteCommandReceived", {
                  forceRebind: true,
                  forceConfigure: true,
                  syncState: false,
                });
              }
              scheduleRemoteCommandStateSync(event);
            }
          }),
          nativePlaybackBridge.addCarConnectionChangedListener((event) => {
            if (!event.connected) {
              return;
            }

            logNativePlayback(
              "[NativePlayback][Front] event carConnectionChanged",
              {
                connected: event.connected,
                surface: event.surface,
              },
            );

            void ensureNativePlaybackSubscription("carConnectionChanged", {
              forceRebind: true,
              forceConfigure: true,
            }).catch((error) => {
              if (!cancelled) {
                store.getState().emit("error", { sourceEvent: error });
              }
            });
          }),
          nativePlaybackBridge.addLimitReachedListener((event) => {
            const storeState = store.getState();
            if (!shouldAcceptNativeAudioEvents(storeState)) {
              logNativePlayback(
                "[NativePlayback][Front] event limitReached ignored: web provider owns current media",
                {
                  identifier: event.identifier,
                  kind: event.kind,
                  limitType: event.limitType,
                  promptType: event.promptType,
                },
              );
              return;
            }

            const prompt = mapNativeLimitReachedEvent(event);
            if (!prompt) {
              logNativePlayback(
                "[NativePlayback][Front] event limitReached ignored: invalid payload",
                event,
              );
              return;
            }

            logNativePlayback("[NativePlayback][Front] event limitReached", prompt);
            storeState.handleNativeLimitReached(prompt);
          }),
        ]);

        if (
          !removePlaybackState ||
          !removeQueueChanged ||
          !removeRemoteCommand ||
          !removeCarConnectionChanged ||
          !removeLimitReached
        ) {
          removePlaybackState?.();
          removeQueueChanged?.();
          removeRemoteCommand?.();
          removeCarConnectionChanged?.();
          removeLimitReached?.();
          logNativePlayback(
            "[NativePlayback][Front] native listeners bind failed",
            {
              reason,
              hasPlaybackStateListener: Boolean(removePlaybackState),
              hasQueueChangedListener: Boolean(removeQueueChanged),
              hasRemoteCommandListener: Boolean(removeRemoteCommand),
              hasCarConnectionChangedListener: Boolean(
                removeCarConnectionChanged,
              ),
              hasLimitReachedListener: Boolean(removeLimitReached),
            },
          );
          return false;
        }

        if (cancelled) {
          removePlaybackState();
          removeQueueChanged();
          removeRemoteCommand();
          removeCarConnectionChanged();
          removeLimitReached();
          return false;
        }

        nativePlaybackListenerRemovers = [
          removePlaybackState,
          removeQueueChanged,
          removeRemoteCommand,
          removeCarConnectionChanged,
          removeLimitReached,
        ];
        nativeEventListenersBoundRef.current = true;
        scheduledNativeSubscriptionRetryRef.current = null;
        logNativePlayback("[NativePlayback][Front] native listeners bound", {
          reason,
        });
        return true;
      };

      const ensureNativePlaybackSubscription = async (
        reason: string,
        options?: {
          forceRebind?: boolean;
          syncState?: boolean;
          forceConfigure?: boolean;
        },
      ): Promise<boolean> => {
        if (nativePlaybackSubscriptionPromise) {
          return nativePlaybackSubscriptionPromise;
        }

        nativePlaybackSubscriptionPromise = (async () => {
          if (!nativePlaybackBridge.isSupported()) {
            return false;
          }

          await configureNativeSession(
            `${reason}:configure`,
            Boolean(options?.forceConfigure || options?.forceRebind),
          );
          if (cancelled) {
            return false;
          }

          if (
            nativeEventListenersBoundRef.current &&
            !options?.forceRebind
          ) {
            return true;
          }

          const didBind = await registerNativePlaybackListeners(reason);
          if (!didBind) {
            scheduleNativePlaybackSubscriptionRetry(reason);
          }
          if (!didBind || cancelled || options?.syncState === false) {
            return didBind;
          }

          if (queueSyncPromiseRef.current) {
            schedulePostSyncRefresh(`${reason}:subscription`);
            return didBind;
          }

          await syncStoreFromNativeState(`${reason}:subscription`, {
            promoteUsableSnapshotToNative: true,
          });
          return didBind;
        })();

        try {
          return await nativePlaybackSubscriptionPromise;
        } finally {
          nativePlaybackSubscriptionPromise = null;
        }
      };

      const triggerNativePlaybackSubscription = (
        reason: string,
        options?: {
          forceRebind?: boolean;
          syncState?: boolean;
          forceConfigure?: boolean;
        },
      ) => {
        void ensureNativePlaybackSubscription(reason, options).catch((error) => {
          if (!cancelled) {
            store.getState().emit("error", { sourceEvent: error });
          }
        });
      };

      const removeAppStateChange = await useAppEventManager
        .addListener("appStateChange", ({ isActive }) => {
          if (!isActive) {
            return;
          }

          logNativePlayback("[NativePlayback][Front] event appStateChange", {
            isActive,
          });
          triggerNativePlaybackSubscription("appStateChange", {
            forceRebind: true,
            forceConfigure: true,
          });
        })
        .then((listenerId) => {
          return () => {
            if (listenerId) {
              useAppEventManager.removeListenerById(listenerId);
            }
          };
        });

      const removeResume = await useAppEventManager
        .addListener("resume", () => {
          logNativePlayback("[NativePlayback][Front] event resume");
          triggerNativePlaybackSubscription("resume", {
            forceRebind: true,
            forceConfigure: true,
          });
        })
        .then((listenerId) => {
          return () => {
            if (listenerId) {
              useAppEventManager.removeListenerById(listenerId);
            }
          };
        });

      const handleWindowFocus = () => {
        logNativePlayback("[NativePlayback][Front] event focus");
        triggerNativePlaybackSubscription("windowFocus", {
          forceRebind: true,
          forceConfigure: true,
        });
      };
      const handleVisibilityChange = () => {
        if (document.visibilityState !== "visible") {
          return;
        }

        logNativePlayback("[NativePlayback][Front] event visibilitychange", {
          visibilityState: document.visibilityState,
        });
        triggerNativePlaybackSubscription("visibilitychange", {
          forceRebind: true,
          forceConfigure: true,
        });
      };

      if (typeof window !== "undefined") {
        window.addEventListener("focus", handleWindowFocus);
        removers.push(() => {
          window.removeEventListener("focus", handleWindowFocus);
        });
      }

      if (typeof document !== "undefined") {
        document.addEventListener("visibilitychange", handleVisibilityChange);
        removers.push(() => {
          document.removeEventListener(
            "visibilitychange",
            handleVisibilityChange,
          );
        });
      }

      removers.push(removeAppStateChange, removeResume, () => {
        disposeNativePlaybackListeners();
        void nativePlaybackBridge.removeAllListeners();
      });

      const didBindNativeListeners = await ensureNativePlaybackSubscription(
        "initialBind",
        {
          forceRebind: true,
          forceConfigure: true,
          syncState: false,
        },
      );
      if (!didBindNativeListeners || cancelled) {
        return;
      }

      await syncStoreFromNativeState("initialGetState");
    }

    void init();

    return () => {
      cancelled = true;
      if (remoteCommandSyncTimeoutId) {
        clearTimeout(remoteCommandSyncTimeoutId);
      }
      nativeSubscriptionRetryTimeoutIds.forEach((timeoutId) => {
        clearTimeout(timeoutId);
      });
      removers.forEach((remove) => remove());
      hasConfiguredSessionRef.current = false;
      lastLanguageSignatureRef.current = null;
      lastFrontendMirroredQueueStructureRef.current = null;
      lastNativeQueueSignatureRef.current = null;
      pendingFrontendQueueSignatureRef.current = null;
      queueSyncPromiseRef.current = null;
      activeQueueSyncRunIdRef.current = null;
      hasBootstrappedNativeStateRef.current = false;
      hasProcessedInitialNativeQueueRef.current = false;
      isHydratingFromNativeRef.current = false;
      nativeEventListenersBoundRef.current = false;
      scheduledPostSyncRefreshRef.current = null;
      scheduledNativeSubscriptionRetryRef.current = null;
      pendingPlaybackEndCheckRef.current = null;
      pendingNativeQueueReconcileRef.current = null;
      nativeQueueRef.current = [];
      queueSnapshotRef.current = null;
      nativeQueueAuthorityRef.current = "unknown";
    };
  }, [
    applyPlaybackState,
    baseConfig,
    getNativeQueueSyncDecision,
    handleRemoteCommand,
    hydrateStoreFromNativeQueue,
    languageOptions.language,
    syncLanguage,
  ]);

  useEffect(() => {
    if (!hasConfiguredSessionRef.current) {
      return;
    }

    let cancelled = false;

    async function syncActiveLanguage() {
      try {
        await syncLanguage();
      } catch (error) {
        if (!cancelled) {
          store.getState().emit("error", { sourceEvent: error });
        }
      }
    }

    void syncActiveLanguage();

    return () => {
      cancelled = true;
    };
  }, [languageSignature, store, syncLanguage]);

  return null;
}

function getStoreQueueForNativePlayback(
  state: Pick<PlayerStoreState, "shuffledQueue" | "cuedMedia">,
): MediaItem[] {
  if (state.shuffledQueue.length) {
    return state.shuffledQueue;
  }

  return state.cuedMedia ? [state.cuedMedia] : [];
}

function shouldAcceptNativeAudioEvents(
  state: Pick<PlayerStoreState, "cuedMedia">,
): boolean {
  return !state.cuedMedia || canUseNativeAudioPlaybackForMedia(state.cuedMedia);
}

function shouldHydrateQueueFromNative(
  state: Pick<PlayerStoreState, "shuffledQueue" | "cuedMedia">,
): boolean {
  const queue = getStoreQueueForNativePlayback(state);
  return !queue.length || canUseNativeAudioPlaybackForQueue(queue);
}

function isNativeQueueAuthoritative(
  state: Pick<PlayerStoreState, "shuffledQueue" | "cuedMedia">,
): boolean {
  return canUseNativeAudioPlaybackForQueue(
    getStoreQueueForNativePlayback(state),
  );
}

function parsePlaybackRestrictionBool(value: unknown): boolean {
  if (value == null || value === "") {
    return true;
  }
  if (typeof value === "boolean") {
    return value;
  }
  if (typeof value === "number") {
    return value !== 0;
  }
  if (typeof value === "string") {
    return !["false", "0", "off", "no"].includes(value.toLowerCase());
  }
  return Boolean(value);
}

function hasUsableNativePlaybackSnapshot(
  snapshot: NativePlaybackStateResponse,
): boolean {
  return Boolean(
    snapshot.queue.items.length > 0 || snapshot.state.currentItem,
  );
}

function createSyntheticQueueSnapshot({
  media,
  snapshot,
  state,
}: {
  media?: MediaItem;
  snapshot: QueueSnapshot;
  state?: Pick<PlaybackState, "currentIndex"> &
    Partial<Pick<PlaybackState, "shuffling" | "repeatMode" | "controlsLocked">>;
}): QueueSnapshot | null {
  if (!media) {
    return null;
  }

  const item = toPlaybackItem(media);
  if (!item) {
    return null;
  }

  return {
    items: [item],
    currentIndex: 0,
    shuffling: state?.shuffling ?? snapshot.shuffling,
    repeatMode: state?.repeatMode ?? snapshot.repeatMode,
    controlsLocked: state?.controlsLocked ?? snapshot.controlsLocked,
  };
}

function createMirroredNativeQueueSnapshot(
  state: Pick<
    PlayerStoreState,
    "shuffledQueue" | "cuedMedia" | "shuffling" | "repeat" | "controlsLocked"
  >,
): { snapshot: QueueSnapshot; mediaQueue: MediaItem[] } | null {
  const sourceQueue = getStoreQueueForNativePlayback(state);
  const currentMedia = canUseNativeAudioPlaybackForMedia(state.cuedMedia)
    ? state.cuedMedia
    : undefined;

  let mediaQueue = sourceQueue.filter((media) =>
    canUseNativeAudioPlaybackForMedia(media),
  );

  if (
    currentMedia &&
    !mediaQueue.some((media) => isSameMedia(media, currentMedia))
  ) {
    mediaQueue = [currentMedia, ...mediaQueue];
  }

  const entries = getNativeQueueEntries(mediaQueue);
  if (!entries.length) {
    return null;
  }

  const normalizedMediaQueue = entries.map((entry) => entry.media);
  const currentIndex = currentMedia
    ? Math.max(
        0,
        normalizedMediaQueue.findIndex((media) =>
          isSameMedia(media, currentMedia),
        ),
      )
    : 0;

  return {
    mediaQueue: normalizedMediaQueue,
    snapshot: {
      items: entries.map((entry) => entry.item),
      currentIndex,
      shuffling: state.shuffling && entries.length > 1,
      repeatMode: toNativeRepeatMode(state.repeat),
      controlsLocked: Boolean(state.controlsLocked || currentMedia?.isAd),
    },
  };
}

function getNativeQueueEntries(queue: MediaItem[]): NativeQueueEntry[] {
  return queue
    .map((media) => {
      const item = toPlaybackItem(media);
      return item ? { media, item } : null;
    })
    .filter((entry): entry is NativeQueueEntry => entry !== null);
}

function resolveNativeQueueMedia(
  queue: MediaItem[],
  state: Pick<PlaybackState, "currentIndex" | "currentItem">,
): MediaItem | undefined {
  const entries = getNativeQueueEntries(queue);
  if (state.currentItem) {
    const matchedMedia = entries.find(
      (entry) =>
        `${entry.media.id}` === `${state.currentItem?.id}` &&
        `${entry.media.groupId ?? ""}` ===
          `${state.currentItem?.groupId ?? ""}`,
    )?.media;
    if (matchedMedia) {
      return matchedMedia;
    }

    return undefined;
  }

  const indexedMedia = entries[state.currentIndex]?.media;
  if (indexedMedia) {
    return indexedMedia;
  }
}

function resolveStoreQueueIndex(
  queue: MediaItem[],
  nativeQueue: MediaItem[],
  nativeIndex: number,
): number {
  if (!Number.isInteger(nativeIndex) || nativeIndex < 0) {
    return -1;
  }

  const targetMedia =
    nativeQueue[nativeIndex] ||
    getNativeQueueEntries(queue)[nativeIndex]?.media;
  if (!targetMedia) {
    return -1;
  }

  return queue.findIndex((media) => isSameMedia(media, targetMedia));
}

function queueSnapshotToMediaQueue(
  snapshot: QueueSnapshot,
  existingMediaLookup?: Map<string, MediaItem>,
): MediaItem[] {
  return snapshot.items
    .map((item) =>
      playbackItemToMediaItem(
        item,
        existingMediaLookup?.get(getPlaybackItemLookupKey(item)),
      ),
    )
    .filter((item): item is MediaItem => item !== null);
}

function resolveMediaFromPlaybackItem(
  item: PlaybackItem | undefined,
  existingMediaLookup?: Map<string, MediaItem>,
): MediaItem | undefined {
  if (!item) {
    return undefined;
  }

  const existingMedia = existingMediaLookup?.get(getPlaybackItemLookupKey(item));
  return playbackItemToMediaItem(item, existingMedia) ?? existingMedia;
}

function playbackItemToMediaItem(
  item: PlaybackItem,
  existingMedia?: MediaItem,
): MediaItem | null {
  const provider = getPlaybackItemProvider(item);
  if (!provider) {
    return null;
  }

  const extras = item.extras as Record<string, unknown> | undefined;
  const playerType = getPlaybackItemPlayerType(item, extras);
  const artworkUrl = item.artworkUrl
    ? withAssetUrl(item.artworkUrl)
    : undefined;
  const initialTime = Number(extras?.initialTimeSeconds);
  const normalizedSrc = withAssetUrl(item.src);
  const meta = buildPlaybackTrackMeta(item, {
    artworkUrl,
    playerType,
    existingMedia,
  });
  const streamType = getPlaybackItemStreamType(item, extras);

  return {
    ...(existingMedia ?? {}),
    id: item.id ?? existingMedia?.id,
    groupId: item.groupId ?? existingMedia?.groupId,
    provider,
    src: normalizedSrc,
    poster: artworkUrl ?? existingMedia?.poster,
    isAd:
      typeof extras?.isAd === "boolean" ? extras.isAd : existingMedia?.isAd,
    playerType,
    initialTime:
      Number.isFinite(initialTime) && initialTime > 0 ? initialTime : undefined,
    streamType,
    meta,
  };
}

function enrichMediaWithPlaybackItem(
  media: MediaItem | undefined,
  item?: PlaybackItem,
): MediaItem | undefined {
  if (!media || !item) {
    return media;
  }

  return playbackItemToMediaItem(item, media) ?? media;
}

function toPlaybackItem(media: MediaItem): PlaybackItem | null {
  if (!canUseNativePlayback(media)) {
    return null;
  }

  const track = media.meta as Record<string, any> | undefined;
  const trackAlbum = isRecord(track?.album) ? track?.album : undefined;
  const durationMs = getDurationMs(media);
  const artworkUrl =
    (typeof track?.artwork_large === "string" && track.artwork_large
      ? track.artwork_large
      : undefined) ||
    (typeof trackAlbum?.artwork_large === "string" && trackAlbum.artwork_large
      ? trackAlbum.artwork_large
      : undefined) ||
    (typeof track?.artwork === "string" && track.artwork
      ? track.artwork
      : undefined) ||
    (typeof trackAlbum?.artwork === "string" && trackAlbum.artwork
      ? trackAlbum.artwork
      : undefined) ||
    (track
      ? getTrackImageSrc(
          track as Parameters<typeof getTrackImageSrc>[0],
          "square",
        )
      : undefined) ||
    media.poster;

  return {
    id: media.id,
    groupId: media.groupId,
    src: withAssetUrl(media.src),
    streamProtocol: getStreamProtocol(media),
    playbackKind: media.playerType === "video" ? "video" : "audio",
    sourcePlaybackKind: media.playerType === "video" ? "video" : "audio",
    audioOnly: media.playerType !== "video",
    title: track?.name || `${media.id}`,
    artist: track?.artists?.[0]?.name,
    album: track?.album?.name,
    artworkUrl: artworkUrl ? withAssetUrl(artworkUrl) : undefined,
    durationMs,
    isLive: isLiveStream(media),
    extras: {
      provider: media.provider,
      playerType: media.playerType || "audio",
      streamType: getMediaStreamType(media),
      initialTimeSeconds: media.initialTime,
      durationMs,
      durationSeconds:
        typeof durationMs === "number" && durationMs > 0
          ? durationMs / 1000
          : undefined,
      available: getExplicitBooleanFlag(track?.available, track?.isAvailable),
      encoded: getExplicitBooleanFlag(track?.encoded, track?.isEncoded),
      isAvailable: getExplicitBooleanFlag(track?.isAvailable, track?.available),
      isEncoded: getExplicitBooleanFlag(track?.isEncoded, track?.encoded),
      exclusivity: getExplicitBooleanFlag(
        track?.exclusivity,
        track?.isExclusivity,
      ),
      isExclusivity: getExplicitBooleanFlag(
        track?.isExclusivity,
        track?.exclusivity,
      ),
      albumAvailable: getExplicitBooleanFlag(trackAlbum?.available),
      albumEncoded: getExplicitBooleanFlag(trackAlbum?.encoded),
      isAd: media.isAd === true,
    },
  };
}

function getPlaybackItemProvider(
  item: PlaybackItem,
): MediaItem["provider"] | null {
  const extras = item.extras as Record<string, unknown> | undefined;
  const provider = extras?.provider;
  if (isMediaProvider(provider)) {
    return provider;
  }

  const playbackKind =
    item.audioOnly || item.sourcePlaybackKind === "audio"
      ? "audio"
      : item.sourcePlaybackKind || item.playbackKind;

  switch (item.streamProtocol) {
    case "dash":
      return playbackKind === "video" ? "dash" : "dashAudio";
    case "progressive":
      return playbackKind === "video" ? "htmlVideo" : "htmlAudio";
    case "hls":
      return playbackKind === "video" ? "hls" : "hlsAudio";
    default:
      return null;
  }
}

function getPlaybackItemPlayerType(
  item: PlaybackItem,
  extras?: Record<string, unknown>,
): string {
  const playerType = extras?.playerType;
  if (typeof playerType === "string" && playerType.length) {
    return playerType;
  }

  if (item.sourcePlaybackKind === "video") {
    return "video";
  }

  return "audio";
}

function getPlaybackItemStreamType(
  item: PlaybackItem,
  extras?: Record<string, unknown>,
): MediaStreamType {
  const streamType = extras?.streamType;
  if (isMediaStreamType(streamType)) {
    return streamType;
  }

  return item.isLive ? "live" : null;
}

function canUseNativePlayback(media: MediaItem): boolean {
  return media.provider !== "youtube";
}

function getStreamProtocol(media: MediaItem): StreamProtocol {
  if (media.provider === "dash" || media.provider === "dashAudio") {
    return "dash";
  }

  if (media.provider === "htmlAudio" || media.provider === "htmlVideo") {
    return "progressive";
  }

  return "hls";
}

function getMediaStreamType(media: MediaItem): MediaStreamType {
  return "streamType" in media ? media.streamType ?? null : null;
}

function isLiveStream(media: MediaItem): boolean {
  const streamType = getMediaStreamType(media);
  return Boolean(streamType && streamType !== "on-demand");
}

function getDurationMs(media: MediaItem): number | undefined {
  const duration = Number(
    (media.meta as Record<string, any> | undefined)?.duration,
  );
  if (!Number.isFinite(duration) || duration <= 0) {
    return undefined;
  }

  return Math.round(duration);
}

function getDurationSeconds(media: MediaItem, state?: PlaybackState): number {
  if (state?.durationMs && state.durationMs > 0) {
    return state.durationMs / 1000;
  }

  return (getDurationMs(media) ?? 0) / 1000;
}

function getCueSignature(media: MediaItem): string {
  return `${media.id}:${media.src}:${media.initialTime ?? 0}`;
}

function getQueueSnapshotSignature(snapshot: QueueSnapshot): string {
  const structure = getQueueStructureSignature(snapshot);

  return [snapshot.currentIndex, structure].join("::");
}

function getQueueStructureSignature(snapshot: QueueSnapshot): string {
  const items = snapshot.items
    .map((item) => {
      const provider =
        (item.extras as Record<string, unknown> | undefined)?.provider ?? "";
      return [
        item.id,
        item.groupId ?? "",
        item.src,
        provider,
        item.sourcePlaybackKind ?? "",
        item.audioOnly ? "1" : "0",
      ].join(":");
    })
    .join("|");

  return [
    snapshot.currentIndex,
    snapshot.shuffling ? "1" : "0",
    snapshot.repeatMode,
    snapshot.controlsLocked ? "1" : "0",
    items,
  ].join("::");
}

function isFrontendQueueSyncPending(
  pendingQueueSignature: string | null,
  queueSyncPromise: Promise<void> | null,
): boolean {
  return Boolean(pendingQueueSignature && queueSyncPromise);
}

function shouldKeepPendingCuedMedia(
  storeState: PlayerStoreState,
  incomingMedia?: MediaItem,
): boolean {
  if (!storeState.showLoader || !storeState.cuedMedia || !incomingMedia) {
    return false;
  }

  return !isSameMedia(storeState.cuedMedia, incomingMedia);
}

function shouldPreserveDisplayedAdMedia(
  displayedMedia: MediaItem | undefined,
  incomingMedia: MediaItem | undefined,
  state: Pick<PlaybackState, "controlsLocked">,
): boolean {
  if (!displayedMedia?.isAd || !state.controlsLocked) {
    return false;
  }

  if (!incomingMedia) {
    return true;
  }

  if (incomingMedia.isAd && isSameMedia(displayedMedia, incomingMedia)) {
    return false;
  }

  return !incomingMedia.isAd;
}

function normalizePlaybackStateWithPrevious(
  nextState: PlaybackState,
  previousState: PlaybackState | null,
): PlaybackState {
  if (!previousState) {
    return nextState;
  }

  const previousItem = previousState.currentItem;
  const nextItem = nextState.currentItem;
  const previousItemIsAd = isAdPlaybackItem(previousItem);
  const nextItemIsAd = isAdPlaybackItem(nextItem);
  const shouldPinPreviousAdItem =
    previousItemIsAd &&
    nextState.controlsLocked &&
    (!nextItem || !nextItemIsAd);
  const shouldReuseCurrentItem =
    shouldPinPreviousAdItem ||
    (!nextItem &&
      !!previousItem &&
      nextState.currentIndex === previousState.currentIndex);
  const shouldReuseAdLock =
    !nextState.controlsLocked &&
    previousState.controlsLocked &&
    previousItemIsAd &&
    nextState.currentIndex === previousState.currentIndex;
  const shouldReuseDuration =
    (!nextState.durationMs || nextState.durationMs <= 0) &&
    previousState.durationMs > 0;

  if (
    !shouldReuseCurrentItem &&
    !shouldReuseAdLock &&
    !shouldReuseDuration
  ) {
    return nextState;
  }

  return {
    ...nextState,
    ...(shouldReuseCurrentItem
      ? { currentItem: previousItem }
      : {}),
    ...(shouldReuseAdLock || shouldPinPreviousAdItem
      ? { controlsLocked: true }
      : {}),
    ...(shouldReuseDuration ? { durationMs: previousState.durationMs } : {}),
  };
}

function createQueueEventPlaybackState(
  queue: QueueSnapshot,
  previousState: PlaybackState | null,
): PlaybackState {
  const currentItem =
    queue.items[queue.currentIndex] ??
    previousState?.currentItem ??
    createEmptyPlaybackState().currentItem;

  return normalizePlaybackStateWithPrevious(
    {
      ...(previousState ?? createEmptyPlaybackState()),
      currentIndex: queue.currentIndex,
      currentItem,
      shuffling: queue.shuffling,
      repeatMode: queue.repeatMode,
      controlsLocked:
        queue.controlsLocked || isAdPlaybackItem(currentItem) ? true : false,
    },
    previousState,
  );
}

function shouldRehydrateFromPlaybackStateChange(
  previousState: PlaybackState | null,
  nextState: PlaybackState,
  currentCuedMedia: MediaItem | undefined,
  currentQueueSnapshot: QueueSnapshot | null,
): boolean {
  if (!nextState.currentItem) {
    return false;
  }

  if (!currentQueueSnapshot?.items.length) {
    return true;
  }

  if (!currentCuedMedia) {
    return true;
  }

  const currentItemChanged =
    normalizeComparableId(previousState?.currentItem?.id) !==
      normalizeComparableId(nextState.currentItem.id) ||
    normalizeComparableId(previousState?.currentItem?.groupId) !==
      normalizeComparableId(nextState.currentItem.groupId);

  const currentIndexChanged =
    previousState?.currentIndex !== nextState.currentIndex;

  const cuedMediaDiffers =
    normalizeComparableId(currentCuedMedia.id) !==
      normalizeComparableId(nextState.currentItem.id) ||
    normalizeComparableId(currentCuedMedia.groupId) !==
      normalizeComparableId(nextState.currentItem.groupId);

  return currentItemChanged || currentIndexChanged || cuedMediaDiffers;
}

function normalizeComparableId(value: string | number | undefined): string {
  return value == null ? "" : `${value}`;
}

function isSamePlaybackItem(
  first?: Pick<PlaybackItem, "id" | "groupId" | "src">,
  second?: Pick<PlaybackItem, "id" | "groupId" | "src">,
): boolean {
  return (
    normalizeComparableId(first?.id) === normalizeComparableId(second?.id) &&
    normalizeComparableId(first?.groupId) ===
      normalizeComparableId(second?.groupId) &&
    `${first?.src ?? ""}` === `${second?.src ?? ""}`
  );
}

function isAdPlaybackItem(item?: PlaybackItem): boolean {
  const extras = item?.extras as Record<string, unknown> | undefined;
  return extras?.isAd === true;
}

function removeAdItemsFromNativeSnapshot(
  snapshot: NativePlaybackStateResponse,
): NativePlaybackStateResponse | null {
  const hasAdItems =
    snapshot.queue.items.some((item) => isAdPlaybackItem(item)) ||
    isAdPlaybackItem(snapshot.state.currentItem);

  if (!hasAdItems) {
    return null;
  }

  const filteredItems = snapshot.queue.items.filter(
    (item) => !isAdPlaybackItem(item),
  );
  const removedItemsBeforeCurrent = snapshot.queue.items
    .slice(0, Math.max(snapshot.state.currentIndex, 0))
    .filter((item) => isAdPlaybackItem(item)).length;
  const currentItemRemoved = isAdPlaybackItem(snapshot.state.currentItem);

  if (!filteredItems.length) {
    return {
      queue: {
        ...snapshot.queue,
        items: [],
        currentIndex: 0,
        controlsLocked: false,
      },
      state: {
        ...snapshot.state,
        currentItem: undefined,
        currentIndex: 0,
        isPlaying: false,
        isBuffering: false,
        positionMs: 0,
        controlsLocked: false,
      },
    };
  }

  const nextCurrentIndex = Math.min(
    Math.max(0, snapshot.state.currentIndex - removedItemsBeforeCurrent),
    filteredItems.length - 1,
  );
  const nextCurrentItem = currentItemRemoved
    ? filteredItems[nextCurrentIndex]
    : snapshot.state.currentItem && !isAdPlaybackItem(snapshot.state.currentItem)
    ? snapshot.state.currentItem
    : filteredItems[nextCurrentIndex];

  return {
    queue: {
      ...snapshot.queue,
      items: filteredItems,
      currentIndex: nextCurrentIndex,
      controlsLocked: false,
    },
    state: {
      ...snapshot.state,
      currentItem: nextCurrentItem,
      currentIndex: nextCurrentIndex,
      controlsLocked: false,
      ...(currentItemRemoved
        ? {
            isPlaying: false,
            isBuffering: false,
            positionMs: 0,
          }
        : {}),
    },
  };
}

function didNativePlaybackAdvancePastEndedState(
  endedState: PlaybackState,
  latestState: PlaybackState,
): boolean {
  if (latestState.isPlaying || latestState.isBuffering) {
    return true;
  }

  if (latestState.currentIndex !== endedState.currentIndex) {
    return true;
  }

  if (!isSamePlaybackItem(endedState.currentItem, latestState.currentItem)) {
    return true;
  }

  return latestState.positionMs < endedState.positionMs - 250;
}

function mergeQueueSnapshotWithPlaybackState(
  snapshot: QueueSnapshot | null,
  state: PlaybackState,
): QueueSnapshot | null {
  if (!snapshot) {
    return null;
  }

  return {
    ...snapshot,
    currentIndex: state.currentIndex,
    shuffling: state.shuffling,
    repeatMode: state.repeatMode,
    controlsLocked: state.controlsLocked ?? snapshot.controlsLocked,
  };
}

function isMediaProvider(value: unknown): value is MediaItem["provider"] {
  return (
    value === "youtube" ||
    value === "htmlAudio" ||
    value === "htmlVideo" ||
    value === "hls" ||
    value === "hlsAudio" ||
    value === "dash" ||
    value === "dashAudio"
  );
}

function isMediaStreamType(value: unknown): value is MediaStreamType {
  return (
    value === null ||
    value === "on-demand" ||
    value === "live" ||
    value === "live:dvr" ||
    value === "ll-live" ||
    value === "ll-live:dvr"
  );
}

function toNativeRepeatMode(repeat: StoreRepeatMode): NativeRepeatMode {
  if (repeat === "all" || repeat === "one") {
    return repeat;
  }

  return "off";
}

function fromNativeRepeatMode(
  mode: NativeRepeatMode | undefined,
): StoreRepeatMode {
  if (mode === "all" || mode === "one") {
    return mode;
  }

  return false;
}

function syncRepeatModeFromNative(
  getState: () => PlayerStoreState,
  mode: NativeRepeatMode | undefined,
) {
  const targetMode = fromNativeRepeatMode(mode);
  let attempts = 0;

  while (getState().repeat !== targetMode && attempts < 3) {
    getState().toggleRepeatMode(true);
    attempts += 1;
  }
}

function syncShuffleFromNative(
  getState: () => PlayerStoreState,
  enabled: boolean,
) {
  const state = getState();
  if (state.shuffling !== enabled) {
    state.toggleShuffling(true);
  }
}

type NativeQueueEntry = {
  media: MediaItem;
  item: PlaybackItem;
};

function buildPlaybackTrackMeta(
  item: PlaybackItem,
  {
    artworkUrl,
    playerType,
    existingMedia,
  }: {
    artworkUrl?: string;
    playerType: string;
    existingMedia?: MediaItem;
  },
): Record<string, unknown> {
  const rawExistingTrack = existingMedia?.meta;
  const existingTrack: Record<string, unknown> = isRecord(rawExistingTrack)
    ? rawExistingTrack
    : {};
  const extras = isRecord(item.extras) ? item.extras : {};
  const existingAlbum = isRecord(existingTrack.album)
    ? existingTrack.album
    : {};
  const existingArtists = Array.isArray(existingTrack.artists)
    ? existingTrack.artists
    : [];
  const itemDurationMs = getPlaybackItemDurationMs(item, extras);
  const fallbackArtists =
    existingArtists.length > 0
      ? existingArtists
      : item.artist
      ? [
          {
            id: 0,
            name: item.artist,
            type: "artist",
            model_type: "artist",
            views: 0,
            plays: 0,
          },
        ]
      : [];
  const available = resolveBooleanFlag(
    [
      extras.available,
      extras.isAvailable,
      existingTrack.available,
      existingTrack.isAvailable,
    ],
    true,
  );
  const encoded = resolveBooleanFlag(
    [
      extras.encoded,
      extras.isEncoded,
      existingTrack.encoded,
      existingTrack.isEncoded,
    ],
    true,
  );
  const exclusivity = resolveBooleanFlag(
    [
      extras.exclusivity,
      extras.isExclusivity,
      existingTrack.exclusivity,
      existingTrack.isExclusivity,
    ],
    false,
  );
  const albumAvailable = resolveBooleanFlag(
    [extras.albumAvailable, existingAlbum.available],
    true,
  );
  const albumEncoded = resolveBooleanFlag(
    [extras.albumEncoded, existingAlbum.encoded],
    true,
  );
  const albumName =
    typeof item.album === "string" && item.album.length
      ? item.album
      : typeof existingAlbum.name === "string"
      ? existingAlbum.name
      : undefined;
  const albumImage =
    artworkUrl ??
    (typeof existingAlbum.image === "string" ? existingAlbum.image : undefined);
  const album =
    Object.keys(existingAlbum).length ||
    albumName ||
    albumImage ||
    "albumAvailable" in extras ||
    "albumEncoded" in extras
      ? {
          ...existingAlbum,
          ...(albumName ? { name: albumName } : {}),
          ...(albumImage ? { image: albumImage } : {}),
          available: albumAvailable,
          encoded: albumEncoded,
        }
      : undefined;

  return {
    ...existingTrack,
    id: item.id ?? existingTrack.id,
    src: withAssetUrl(item.src),
    name:
      (typeof item.title === "string" && item.title.length
        ? item.title
        : existingTrack.name) ?? `${item.id}`,
    model_type:
      typeof existingTrack.model_type === "string"
        ? existingTrack.model_type
        : "track",
    player_type: playerType,
    duration: itemDurationMs ?? existingTrack.duration,
    image:
      artworkUrl ??
      (typeof existingTrack.image === "string"
        ? existingTrack.image
        : undefined),
    artists: fallbackArtists,
    album,
    tags: Array.isArray(existingTrack.tags) ? existingTrack.tags : [],
    available,
    encoded,
    exclusivity,
    isAvailable: available,
    isEncoded: encoded,
    isExclusivity: exclusivity,
  };
}

function createMediaLookup(queues: MediaItem[][]): Map<string, MediaItem> {
  const lookup = new Map<string, MediaItem>();

  queues.forEach((queue) => {
    queue.forEach((media) => {
      lookup.set(getMediaLookupKey(media), media);
    });
  });

  return lookup;
}

function getPlaybackItemLookupKey(
  item: Pick<PlaybackItem, "id" | "src">,
): string {
  return `${item.id}::${normalizeMediaSrc(item.src)}`;
}

function getMediaLookupKey(media: Pick<MediaItem, "id" | "src">): string {
  return `${media.id}::${normalizeMediaSrc(media.src)}`;
}

function normalizeMediaSrc(src?: string): string {
  return src ? withAssetUrl(src) : "";
}

function isRecord(value: unknown): value is Record<string, any> {
  return typeof value === "object" && value !== null;
}

function resolveBooleanFlag(values: unknown[], fallback: boolean): boolean {
  for (const value of values) {
    const normalized = normalizeBooleanFlag(value);
    if (typeof normalized === "boolean") {
      return normalized;
    }
  }

  return fallback;
}

function getPlaybackItemDurationMs(
  item: PlaybackItem,
  extras?: Record<string, unknown>,
): number | undefined {
  const durationMsCandidates = [item.durationMs, extras?.durationMs];
  for (const candidate of durationMsCandidates) {
    const value = Number(candidate);
    if (Number.isFinite(value) && value > 0) {
      return value;
    }
  }

  const durationSecondsCandidates = [extras?.durationSeconds];
  for (const candidate of durationSecondsCandidates) {
    const value = Number(candidate);
    if (Number.isFinite(value) && value > 0) {
      return Math.round(value * 1000);
    }
  }

  return undefined;
}

function getExplicitBooleanFlag(...values: unknown[]): boolean | undefined {
  for (const value of values) {
    const normalized = normalizeBooleanFlag(value);
    if (typeof normalized === "boolean") {
      return normalized;
    }
  }

  return undefined;
}

function normalizeBooleanFlag(value: unknown): boolean | undefined {
  if (typeof value === "boolean") {
    return value;
  }

  if (value === 1 || value === "1" || value === "true") {
    return true;
  }

  if (value === 0 || value === "0" || value === "false") {
    return false;
  }

  return undefined;
}

function getStorageBaseUrl(): string {
  const marker = "__native_playback_probe__";
  const url = withAssetUrl(`storage/${marker}`);
  return url.endsWith(marker) ? url.slice(0, -marker.length) : url;
}

function getLanguageSignature(options: NativePlaybackLanguageOptions): string {
  return JSON.stringify({
    language: options.language ?? "",
    locale: options.locale ?? "",
    siteName: options.siteName ?? options.site_name ?? "",
    translations: options.translations ?? {},
  });
}
